import { useCallback, useMemo, useState } from 'react'
import { KpiCard, KpiStrip } from '../../../cc'
import { ReportTable, ReportTablePagination } from '../../../cc/ReportTable'
import { RankedBars } from '../../charts/RankedBars'
import { useReportTableState } from '../../useReportTableState'
import { toApiParams } from '../alertFilters'
import { fmtInt, kindLabel, pct, plural } from '../alertLabels'
import type { ByLocationRow } from '../alertTypes'
import { useAlertAnalytics } from '../useAlertAnalytics'
import { AlertDrillList, type DrillScope } from './AlertDrillList'
import { asRows, ClickHint, columnDefs, makeExport, useViewExport, ViewCard, ViewState, type LocationsMapSlot, type ViewProps } from './viewKit'

const COLUMNS = [
  'place', 'zone', 'alerts', 'critical', 'vehicles', 'drivers', 'top_kind',
  'median_speed_kph', 'median_limit_kph', 'lat', 'lon',
] as const

export interface LocationsViewProps extends ViewProps {
  /** Injection slot for the map (map/LocationMap via ConnectedAlertsReport): given the
   *  loaded bins and the filter handle, returns the map element to render above the
   *  ranking. Absent -> no map, no gap. */
  locationsMap?: LocationsMapSlot
}

/** The /by-location bin size this tab asks for (server default). */
const BIN_DEG = 0.01

type Bin = Pick<ByLocationRow, 'place' | 'zone' | 'lat' | 'lon'>

/** Where alerts cluster: hub or 'en route', overspeed zone, and a 0.01° lat/lon bin
 *  (~1.1 km) so the same bend shows up as one hotspot rather than fifty points. */
export function LocationsView({ filters, setFilters, locationsMap }: LocationsViewProps): JSX.Element {
  const params = useMemo(() => toApiParams(filters), [filters])
  const { rows, status, error } = useAlertAnalytics<ByLocationRow>('/by-location', params)

  const totals = useMemo(() => {
    const alerts = rows.reduce((s, r) => s + r.alerts, 0)
    const critical = rows.reduce((s, r) => s + r.critical, 0)
    const enRoute = rows.filter((r) => r.place === 'en route').reduce((s, r) => s + r.alerts, 0)
    const places = new Set(rows.map((r) => r.place)).size
    const zones = new Set(rows.map((r) => r.zone).filter(Boolean)).size
    return { alerts, critical, enRoute, places, zones }
  }, [rows])

  // Rank by place + zone (bins collapsed) -- a bend with three adjacent bins is one place
  // to a dispatcher, and the map below is where individual bins earn their keep.
  const ranked = useMemo(() => {
    const byKey = new Map<string, { place: string; zone: string | null; alerts: number; critical: number; vehicles: number; top: Map<string, number> }>()
    for (const r of rows) {
      const key = `${r.place}|${r.zone ?? ''}`
      const cur = byKey.get(key) ?? { place: r.place, zone: r.zone, alerts: 0, critical: 0, vehicles: 0, top: new Map() }
      cur.alerts += r.alerts
      cur.critical += r.critical
      cur.vehicles = Math.max(cur.vehicles, r.vehicles)
      if (r.top_kind) cur.top.set(r.top_kind, (cur.top.get(r.top_kind) ?? 0) + r.alerts)
      byKey.set(key, cur)
    }
    return [...byKey.values()]
      .sort((a, b) => b.alerts - a.alerts)
      .slice(0, 10)
      .map((g) => {
        const topKind = [...g.top.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null
        const label = g.zone ? `${g.place} · ${g.zone}` : g.place
        const active = filters.hub === g.place
        return {
          label,
          value: g.alerts,
          sub: `${fmtInt(g.critical)} critical · ≥ ${plural(g.vehicles, 'vehicle')} · top ${kindLabel(topKind)}`,
          tone: g.alerts > 0 && g.critical / g.alerts > 0.5 ? 'danger' as const : g.alerts > 0 && g.critical / g.alerts > 0.2 ? 'warn' as const : 'neutral' as const,
          active,
          onClick: () => setFilters({ hub: active ? '' : g.place }),
        }
      })
  }, [rows, filters.hub, setFilters])

  // A hotspot (map bubble or table row) narrows the alert list below to its ≈1 km bin --
  // the same FLOOR(lat / 0.01) cell the server grouped it by.
  const [bin, setBin] = useState<Bin | null>(null)
  const selectBin = useCallback((r: Bin) => {
    setBin((cur) => (cur && cur.place === r.place && cur.lat === r.lat && cur.lon === r.lon ? null : r))
  }, [])
  const scope: DrillScope | null = useMemo(() => {
    if (!bin) return null
    const params: Record<string, string> = { hub: bin.place }
    if (bin.lat !== null && bin.lon !== null) {
      params.cell_lat = String(Math.floor(bin.lat / BIN_DEG))
      params.cell_lon = String(Math.floor(bin.lon / BIN_DEG))
      params.bin_deg = String(BIN_DEG)
    }
    const at = bin.lat !== null && bin.lon !== null ? ` (${bin.lat.toFixed(3)}, ${bin.lon.toFixed(3)})` : ''
    return { label: `${bin.zone ? `${bin.place} · ${bin.zone}` : bin.place}${at}`, params, onClear: () => setBin(null) }
  }, [bin])

  const table = useReportTableState('alerts-by-location', asRows(rows))
  const defs = useMemo(() => columnDefs(COLUMNS, {
    lat: { align: 'right', render: (v) => <span className="font-mono text-[11px] tabular-nums text-slate-500">{typeof v === 'number' ? v.toFixed(4) : '—'}</span> },
    lon: { align: 'right', render: (v) => <span className="font-mono text-[11px] tabular-nums text-slate-500">{typeof v === 'number' ? v.toFixed(4) : '—'}</span> },
  }), [])
  const exportFn = useMemo(
    () => makeExport(rows, [...COLUMNS], 'alerts-by-location', 'Alerts by location', filters),
    [rows, filters],
  )
  useViewExport(exportFn)

  return (
    <ViewState
      status={status} error={error} rowCount={rows.length} filters={filters} setFilters={setFilters}
      noData={{ title: 'No alerts in this window', body: 'Nothing fired for this site between these dates, so there is nothing to place on a map.' }}
    >
      <div className="space-y-4">
        <KpiStrip>
          <KpiCard label="Alerts" value={totals.alerts} />
          <KpiCard label="Critical" value={totals.critical} tone={totals.critical > 0 ? 'danger' : 'neutral'} dot={totals.critical > 0} />
          <KpiCard label="Places" value={totals.places} sub={`${fmtInt(totals.zones)} overspeed zones touched`} />
          <KpiCard label="Hotspots" value={rows.length} sub="≈1 km bins with ≥ 1 alert" />
          <KpiCard label="En route" value={pct(totals.enRoute, totals.alerts)} unit="%" sub="between hubs, not inside one" tone={pct(totals.enRoute, totals.alerts) > 60 ? 'warn' : 'neutral'} />
        </KpiStrip>

        {locationsMap ? (
          <ViewCard
            title="On the map"
            sub="One bubble per ≈1 km bin, sized by alerts and coloured by its critical share (blue → amber → red). Click a bubble to list its alerts below."
            bodyClassName="p-0"
          >
            <div className="h-80 w-full">{locationsMap(rows, { filters, setFilters, selectBin })}</div>
          </ViewCard>
        ) : null}

        <ViewCard title="Top places" sub="Hub or 'en route', split by overspeed zone where one applies.">
          <ClickHint>Click a place to focus every tab on it; click again to release.</ClickHint>
          <RankedBars items={ranked} ariaLabel="Top places by alert count" />
        </ViewCard>

        <AlertDrillList
          filters={filters}
          setFilters={setFilters}
          scope={scope}
          title={bin ? 'Alerts in this hotspot' : undefined}
          sub={bin ? 'Every alert inside this ≈1 km square. Click one to see it on the map, inside its trip.' : undefined}
          idleHint="Click a bubble on the map or a hotspot row below to see just its alerts."
        />

        <ViewCard title="Every hotspot" sub="One row per place, zone and ≈1 km bin, with the median speed and limit of the overspeed alerts in it. Click a row to list its alerts." bodyClassName="p-0">
          <ReportTable
            columns={[...COLUMNS]}
            rows={table.pageRows}
            columnDefs={defs}
            sort={table.sort}
            onSort={table.toggleSort}
            onRowClick={(row) => selectBin({
              place: String(row.place),
              zone: typeof row.zone === 'string' ? row.zone : null,
              lat: typeof row.lat === 'number' ? row.lat : null,
              lon: typeof row.lon === 'number' ? row.lon : null,
            })}
            rowOffset={table.page * (table.pageSize || table.sorted.length)}
            compact
            applyLoadStateLabels={false}
            className="rounded-none ring-0 shadow-none"
          />
          {table.totalCount > table.pageSize && table.pageSize > 0 ? (
            <div className="border-t border-slate-200 p-3">
              <ReportTablePagination
                page={table.page}
                totalPages={table.totalPages}
                pageSize={table.pageSize}
                pageSizes={table.pageSizes}
                showingFrom={table.showingFrom}
                showingTo={table.showingTo}
                totalCount={table.totalCount}
                onPageChange={table.setPage}
                onPageSizeChange={(s) => { table.setPageSize(s); table.setPage(0) }}
              />
            </div>
          ) : null}
        </ViewCard>
      </div>
    </ViewState>
  )
}
